/**
 * Monaco configuration dispatcher. Called from ProblemDetailPage on editor
 * mount and whenever the language changes.
 *
 * Behavior:
 *   - configureMonacoBase runs ONCE per page load: it tunes the TypeScript
 *     language service (which covers both TS and JS).
 *   - ensureLanguageProviders is called on mount AND on every language
 *     switch. Currently this only matters for Python: the Python provider
 *     has to be registered exactly once. Calling it twice would duplicate
 *     completions. We track registration state via a per-monaco-instance
 *     WeakSet so we never double-register.
 *
 * Each call returns a disposable. Callers should invoke `.dispose()` on
 * unmount for cleanliness, though the providers are cheap to leak.
 */
import type { Monaco } from '@monaco-editor/react';
import { configureTypeScript } from './typescript.js';
import { registerPythonCompletion } from './python.js';

// Track which Monaco instances we've already bootstrapped so double-mounts
// (e.g. React StrictMode in dev) don't duplicate registrations.
const configured = new WeakSet<Monaco>();
const pythonRegistered = new WeakSet<Monaco>();

export interface EditorDisposable {
  dispose: () => void;
}

/**
 * One-time Monaco setup: TS/JS service config + Python provider registration.
 * Safe to call multiple times; idempotent via WeakSet guards.
 */
export function configureMonaco(monaco: Monaco): EditorDisposable {
  const disposables: EditorDisposable[] = [];

  if (!configured.has(monaco)) {
    disposables.push(configureTypeScript(monaco));
    configured.add(monaco);
  }

  if (!pythonRegistered.has(monaco)) {
    disposables.push(registerPythonCompletion(monaco));
    pythonRegistered.add(monaco);
  }

  return {
    dispose() {
      for (const d of disposables) {
        try {
          d.dispose();
        } catch {
          // ignore
        }
      }
    },
  };
}
